import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiShield, FiTrendingUp } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiShield,
      title: 'Quality Assurance',
      description: 'We ensure all our products meet the highest halal standards and quality requirements.'
    },
    {
      icon: FiHeart,
      title: 'Community Focus',
      description: 'Serving the Gateshead community with dedication and understanding of cultural needs.'
    },
    {
      icon: FiUsers,
      title: 'Friendly Service',
      description: 'Our experienced staff provides personalized service and product recommendations.'
    },
    {
      icon: FiTrendingUp,
      title: 'Continuous Growth',
      description: 'We constantly expand our product range to meet evolving customer needs.'
    }
  ];

  const images = [
    'https://i.ibb.co/PZ2ht1Pt/Gateshead-Halal-Food-Store-image-9.jpg',
    'https://i.ibb.co/jvYvY1KB/Gateshead-Halal-Food-Store-image-11.jpg',
    'https://i.ibb.co/fdrHm15H/Gateshead-Halal-Food-Store-image-15.jpg'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-secondary-600 via-primary-600 to-accent-600 bg-clip-text text-transparent mb-4">
            About Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn more about Gateshead Halal Food Store and our commitment to serving quality halal products.
          </p>
        </motion.div>

        {/* Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div variants={itemVariants}>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-6">Our Story</h2>
            <p className="text-gray-700 mb-4">
              Gateshead Halal Food Store has been a cornerstone of the local community, providing fresh halal meat, 
              Asian groceries, and Mediterranean products to families throughout Gateshead and surrounding areas.
            </p>
            <p className="text-gray-700 mb-4">
              Our commitment to quality and authenticity has made us the trusted choice for customers seeking 
              genuine halal products. We understand the importance of halal certification and work with certified 
              suppliers to ensure all our meat products meet strict halal standards.
            </p>
            <p className="text-gray-700">
              From our famous lamb shawarma to fresh vegetables and specialty items, we pride ourselves on 
              offering diverse products that cater to the multicultural community we serve.
            </p>
          </motion.div>
          <motion.div 
            variants={itemVariants}
            className="grid grid-cols-1 gap-4"
          >
            {images.map((image, index) => (
              <motion.img
                key={index}
                src={image}
                alt={`About Image ${index + 1}`}
                className="rounded-lg shadow-lg w-full h-48 object-cover border-2 border-primary-200"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
              />
            ))}
          </motion.div>
        </div>

        {/* Values Section */}
        <motion.div variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow border border-primary-200"
                whileHover={{ y: -5 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-secondary-100 via-primary-100 to-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* What is Halal Section */}
        <motion.div 
          variants={itemVariants}
          className="bg-gradient-to-br from-accent-50 via-primary-50 to-secondary-50 rounded-lg p-8 mb-16 border border-primary-200"
        >
          <h2 className="text-3xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent text-center mb-8">What is Halal Food?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Halal Meat</h3>
              <p className="text-gray-700 mb-4">
                Halal meat comes from animals that have been slaughtered according to Islamic law. 
                This includes specific prayers and methods that ensure the meat is permissible for consumption.
              </p>
              <ul className="text-gray-700 space-y-2">
                <li>• Animals must be healthy at the time of slaughter</li>
                <li>• Slaughter must be performed by a Muslim</li>
                <li>• Specific prayers must be recited</li>
                <li>• Blood must be completely drained</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Halal Fruits & Vegetables</h3>
              <p className="text-gray-700 mb-4">
                All natural fruits and vegetables are considered halal. We ensure our produce is fresh, 
                clean, and free from any prohibited substances or cross-contamination.
              </p>
              <ul className="text-gray-700 space-y-2">
                <li>• Fresh, organic when possible</li>
                <li>• Properly cleaned and prepared</li>
                <li>• No cross-contamination with non-halal items</li>
                <li>• Sourced from trusted suppliers</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          variants={itemVariants}
          className="text-center bg-gradient-to-r from-secondary-600 via-primary-600 to-accent-600 rounded-lg p-8 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Visit Us Today</h2>
          <p className="text-xl mb-6">
            Experience the difference of authentic halal products and friendly service.
          </p>
          <a
            href="tel:+447460008191"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
          >
            Call +447460008191
          </a>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default About;